import { queryCache, useMutation, useQuery } from 'react-query';
import { ApplicationDispatch } from '../store/application/types';
import { fetchData, postData, fetchFile, postFile } from './fetch';
import { IApplicationDocumentType } from '../Interfaces/Application/IApplicationDocumentType';
import { IApplicationDocument } from '../Interfaces/Application/IApplicationDocument';
import { saveFile } from '../utils/utils';

export const useQueryApplicationDocumentFile = (dispatch: ApplicationDispatch, documentId?: string, fileName?: string, preSignedUrl?: string) => {
  const downloadFile = async (key: string, documentId?: string, fileName?: string, preSignedUrl?: string) => {
    // If the documentId or fileName are not provided, return an empty string.
    if (!documentId || !fileName) {
      return '';
    }
    // Use pre-signed URL directly if available, bypassing byte download.
    if (preSignedUrl) {
      return preSignedUrl;
    }
    const url = `api/applicationdocument/download/${documentId}`;
    const response = await fetchFile(url, dispatch);
    const blob = await response.blob();
    // if the API fails, return an empty string.
    if (!blob) {
      return '';
    }
    // Create a URL for the blob.
    const objectUrl = window.URL.createObjectURL(blob);
    return objectUrl;
  }

  return useQuery(
    ['ApplicationDocument', documentId, fileName, preSignedUrl],
    downloadFile,
    {
      staleTime: Infinity,
    });
}

export interface ApplicationDocumentMutation {
  applicationId: string;
  documentId: number;
  name: string;
  applicationDocumentTypeIds?: number[];
  notes?: string;
  allowCustomerToViewInPortal: boolean;
}

export const useMutateApplicationDocument = () => {
  const updateApplicationDocument = async (variables: ApplicationDocumentMutation) => {
    const url = `api/applicationdocument/update/${variables.documentId}`;
    const jsonResponse = await postData(url, variables);
    return jsonResponse;
  }

  return useMutation(updateApplicationDocument, {
    onSuccess: async (data, variables) => {
      await queryCache.refetchQueries(["applicationDocuments"]);
    }
  })
}


export const requestDocuments = async (appId: string, toNumber: string, type: string) => {
  const url = `api/applicationdocument/request/${appId}?toNumber=${toNumber}&type=${type}`;
  const jsonResponse = await postData(url);
  return jsonResponse;
}

export const useApplicationDocumentTypes = (dispatch: ApplicationDispatch) => {
  const { data: applicationDocumentTypes } = useQuery(
    ["applicationDocumentTypes"],
    async () => {
      return await getApplicationDocumentTypes(dispatch);
    },
    {
      refetchOnWindowFocus: false,
    }
  );
  return applicationDocumentTypes;
}

export const useApplicationDocuments = (dispatch: ApplicationDispatch, applicationId: string) => {
  const { data: documentsFromServer } = useQuery(
    ["applicationDocuments", applicationId],
    async () => {
      if (applicationId) {
        const result: Document[] = await listApplicationDocuments(applicationId, dispatch);
        return result;
      } else {
        return undefined;
      }
    },
    {
      refetchOnWindowFocus: false,
      refetchInterval: 15_000, // Refetch every 15 seconds.
    }
  );
  return documentsFromServer;
}

/**
 * Finicity (income verification) documents for the application. Pass `enabled = false` when the client does not use
 * Finicity (FinicityEnabled feature flag) or the user lacks the permission: the query then never fetches or polls,
 * so clients without the integration do not hit the API every 15 seconds for every open application.
 */
export const useApplicationFinicityDocuments = (dispatch: ApplicationDispatch, applicationId: string, enabled: boolean = true) => {
  const { data: documentsFromServer } = useQuery(
    ["incomeVerificationDocuments", applicationId, enabled],
    async () => {
      if (applicationId && enabled) {
        const result: Document[] = await listApplicationFinicityDocuments(applicationId, dispatch);
        return result;
      } else {
        return undefined;
      }
    },
    {
      refetchOnWindowFocus: false,
      // react-query 1.x: `manual` disables the automatic fetch; a false interval stops the polling timer.
      manual: !enabled,
      refetchInterval: enabled ? 15_000 : false, // Refetch every 15 seconds while enabled.
    }
  );
  return documentsFromServer;
}

const getApplicationDocumentTypes = async (dispatch: ApplicationDispatch) => {
  const url = `api/applicationdocument/types`;
  const json: IApplicationDocumentType[] = await fetchData(url, dispatch);
  return json;
}

const listApplicationDocuments = async (applicationId: string, dispatch: ApplicationDispatch) => {
  const url = `api/applicationdocument/list/${applicationId}`;
  const json: IApplicationDocument[] = await fetchData(url, dispatch);
  return json;
}

const listApplicationFinicityDocuments = async (applicationId: string, dispatch: ApplicationDispatch) => {
  const url = `api/applicationdocument/list/finicity/${applicationId}`;
  const json: IApplicationDocument[] = await fetchData(url, dispatch);
  return json;
}

export interface Document extends IApplicationDocument {
  file?: File;
  error?: string;
  isNew?: boolean;
}

export const deleteApplicationDocument = async (applicationDocumentId: number) => {
  const url = `api/applicationdocument/delete/${applicationDocumentId}`;
  const json: IApplicationDocument = await postData(url, {});
  return;
}

export const uploadFile = async (applicationId: string, file: File) => {
  const url = `api/applicationdocument/upload/${applicationId}/${encodeURIComponent(file.name)}`;
  const response = await postFile(url, file);

  if (!response.ok) {
    const json = await response.json();
    const msg = `Unable to upload file: ${json?.error}`;
    throw new Error(msg);
  }
  const json: IApplicationDocument = await response.json();
  return json;
}

export const downloadFile = async (documentId: string, guessedFileName: string, dispatch: ApplicationDispatch) => {
  const url = `api/applicationdocument/download/${documentId}`;
  const response = await fetchFile(url, dispatch);
  if (!response.ok) {
    const json = await response.json();
    const msg = `Unable to download file: ${JSON.stringify(json)}`;
    dispatch({ type: "SET_SERVER_MESSAGE", payload: { message: msg, isError: true } });
    return; /* quity early */
  }
  await saveFile(response, guessedFileName);
}
